import gql from "graphql-tag";
import * as React from "react";
import { useMutation } from "react-apollo";
import { PlayoutSecuritySettings } from "./ChannelPlayoutSecurity";
import {
    UpdateGeoblockingChannelMutation,
    UpdateGeoblockingChannelMutationVariables,
} from "./__generated__/UpdateGeoblockingChannelMutation";
import {
    UpdateGeoblockingChannelAliasMutation,
    UpdateGeoblockingChannelAliasMutationVariables,
} from "./__generated__/UpdateGeoblockingChannelAliasMutation";
import { toast, ToastContainer } from "react-toastify";
import { TOAST_SETTINGS } from "../../BillingDetails/AddPaymentMethodDialog";
import { GET_CHANNEL_QUERY } from "../Overview/channelQueriesMutations";
import { BlockingModeEnum } from "../../../__generated__/globalTypes";
import GeoBlockingModeSelector from "./GeoBlockingModeSelector";
import HESPFormLabel from "../../HESPFormLabel/HESPFormLabel";
import ContentSettingsWithSwitchCard from "./ContentSettingsWithSwitchCard";
import CIDRManager from "../../CIDRManager/CIDRManager";

export const UPDATE_IPBLOCKING_CHANNEL_MUTATION = gql`
    mutation UpdateIPblockingChannelMutation($input: UpdateChannelInput!) {
        updateChannel(input: $input) {
            channelId
        }
    }
`;

export const UPDATE_IPBLOCKING_CHANNEL_ALIAS_MUTATION = gql`
    mutation UpdateIPlockingChannelAliasMutation($input: UpdateChannelAliasInput!) {
        updateChannelAlias(input: $input) {
            channelId
        }
    }
`;

interface Props {
    securitySettings: PlayoutSecuritySettings;
    organizationId: string;
    disabled?: boolean;
}

function ChannelPlayoutSecurityIPBlocking({ securitySettings, organizationId, disabled }: Props) {
    const initialConfig = securitySettings.ipBlocking;
    const [currentId, setCurrentId] = React.useState<string | undefined>(securitySettings.channelId);

    React.useEffect(() => {
        if (!currentId || currentId !== securitySettings.channelId) {
            setEnabled(initialConfig.enabled);
            setCidrs(initialConfig.cidrs);
            setMode(initialConfig.mode);
            setCurrentId(securitySettings.channelId);
        }
    }, [initialConfig, securitySettings.channelId, currentId]);

    const [enabled, setEnabled] = React.useState<boolean>(initialConfig.enabled);
    const [mode, setMode] = React.useState<BlockingModeEnum>(initialConfig.mode);
    const [cidrs, setCidrs] = React.useState<string[]>(initialConfig.cidrs);
    const [needsSave, setNeedsSave] = React.useState<boolean>(false);
    const [showAtLeastOneCidrError, setShowAtLeastOneCidrsError] = React.useState(false);

    const [updateIPblockingChannel, { loading: loadingUpdateChannelMut }] = useMutation<
        UpdateGeoblockingChannelMutation,
        UpdateGeoblockingChannelMutationVariables
    >(UPDATE_IPBLOCKING_CHANNEL_MUTATION);

    const [updateIPblockingChannelAlias, { loading: loadingUpdateChannelAliasMut }] = useMutation<
        UpdateGeoblockingChannelAliasMutation,
        UpdateGeoblockingChannelAliasMutationVariables
    >(UPDATE_IPBLOCKING_CHANNEL_ALIAS_MUTATION);

    function onEnabledChange() {
        setEnabled(!enabled);
        setNeedsSave(true);
    }

    function onDiscard() {
        setEnabled(initialConfig.enabled);
        setCidrs(initialConfig.cidrs);
        setMode(initialConfig.mode);
        setNeedsSave(false);
    }

    function onChangeMode(newMode: BlockingModeEnum) {
        setNeedsSave(true);
        setMode(newMode);
    }

    async function onSave() {
        setShowAtLeastOneCidrsError(false);
        if (enabled && cidrs.length === 0) {
            setShowAtLeastOneCidrsError(true);
        } else {
            try {
                if (!securitySettings.isAlias) {
                    await updateIPblockingChannel({
                        variables: {
                            input: {
                                channelId: securitySettings.channelId,
                                organizationId,
                                publicationUpdates: {
                                    ipBlockingUpdates: {
                                        cidrs,
                                        enabled,
                                        mode,
                                    },
                                },
                            },
                        },
                        refetchQueries: [
                            {
                                query: GET_CHANNEL_QUERY,
                                variables: {
                                    channelId: securitySettings.parentChannelId,
                                    organizationId,
                                },
                            },
                        ],
                    });
                } else {
                    await updateIPblockingChannelAlias({
                        variables: {
                            input: {
                                channelAliasId: securitySettings.channelId,
                                organizationId,
                                channelId: securitySettings.parentChannelId,
                                publicationUpdates: {
                                    ipBlockingUpdates: {
                                        cidrs,
                                        enabled,
                                        mode,
                                    },
                                },
                            },
                        },
                        refetchQueries: [
                            {
                                query: GET_CHANNEL_QUERY,
                                variables: {
                                    channelId: securitySettings.parentChannelId,
                                    organizationId,
                                },
                            },
                        ],
                    });
                }
                toast.success("IP blocking config updated! 🚀", TOAST_SETTINGS);
            } catch (_ignore) {
                toast.error("Something went wrong 😞", TOAST_SETTINGS);
            } finally {
                setNeedsSave(false);
            }
        }
    }

    return (
        <>
            <ContentSettingsWithSwitchCard
                title="IP blocking"
                subtitle="Limit which IP addresses can access this playout"
                enabled={enabled}
                onSaveClick={onSave}
                needsSave={!disabled && needsSave}
                showSwitch
                onEnabledChange={onEnabledChange}
                onDiscardClick={onDiscard}
                isLoading={loadingUpdateChannelMut || loadingUpdateChannelAliasMut}
            >
                <HESPFormLabel label="Mode:" />
                <GeoBlockingModeSelector
                    mode={mode}
                    onChange={(newMode: BlockingModeEnum) => onChangeMode(newMode)}
                    whitelistText="Only CIDRs in the list will have access to the stream"
                    blacklistText="Stream will be blocked for CIDRs in the list"
                />
                {showAtLeastOneCidrError && <div style={{ color: "red" }}>At least one CIDR should be passed</div>}
                <HESPFormLabel label="CIDRs (IPv4 and IPv6 allowed):" />
                <CIDRManager
                    cidrs={cidrs}
                    onAdd={(cidr: string) => {
                        setNeedsSave(true);
                        setCidrs([...cidrs, cidr]);
                    }}
                    onDelete={(cidr: string) => {
                        setNeedsSave(true);
                        setCidrs(cidrs.filter((c) => c !== cidr));
                    }}
                />
            </ContentSettingsWithSwitchCard>
            <ToastContainer />
        </>
    );
}

export default ChannelPlayoutSecurityIPBlocking;
